"use client"

import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { Button } from "../../ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table"
import { AddEditTemplateModal } from "../../modals/add-edit-template-modal"
import { apiClient } from "../../lib/apiClient"
import { useToast } from "../../hooks/use-toast"
import { FileText, Plus, Edit2, Loader2 } from "lucide-react"

interface Template {
  id: number
  name: string
  templateText: string
  modifiedBy: number
  modifiedOn: string
}

interface User {
  userID: number
  displayName: string
  email: string
}

export function TemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null)
  const { toast } = useToast()

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get("/Admin/GetTemplates")
      const data = Array.isArray(response.data) ? response.data : []
      setTemplates(data)
    } catch (err: any) {
      console.error("Error fetching templates:", err)
      setError(err?.response?.data?.message || err?.message || "Failed to load templates")
    } finally {
      setLoading(false)
    }
  }

  // Fetch users to map modifiedBy IDs to names
  const fetchUsers = async () => {
    try {
      const response = await apiClient.get("/user/getallusers")
      const data = Array.isArray(response.data) 
        ? response.data 
        : (Array.isArray(response.data?.users) ? response.data.users : [])
      setUsers(data)
    } catch (err: any) {
      console.error("Error fetching users:", err)
      setUsers([])
    }
  }

  useEffect(() => {
    fetchUsers().then(() => {
      fetchTemplates()
    })
  }, [])

  // Get user name from ID
  const getUserName = (userId: number): string => {
    const user = users.find(u => (u.userID || (u as any).UserID) === userId)
    return user?.displayName || user?.email || `User ${userId}` || "--"
  }

  // Format date and time
  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return "--"
    try {
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return "--"
    }
  }

  // Truncate template text for display
  const truncateText = (text: string, maxLength: number = 100): string => {
    if (!text) return "--"
    // Remove HTML tags for display
    const plainText = text.replace(/<[^>]*>/g, '')
    if (plainText.length <= maxLength) return plainText
    return plainText.substring(0, maxLength) + "..."
  }

  const handleAddClick = () => {
    setEditingTemplate(null)
    setIsModalOpen(true)
  }

  const handleEditClick = (template: Template) => {
    setEditingTemplate(template)
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setEditingTemplate(null)
  }

  const handleSuccess = () => {
    fetchTemplates()
  }

  if (loading && templates.length === 0) {
    return (
      <div className="space-y-6">
        <Card className="w-full bg-white border border-gray-200 rounded-lg shadow-sm">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-4" />
            <p className="text-gray-500">Loading templates...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card className="w-full bg-white border border-gray-200 rounded-lg shadow-sm">
        <CardHeader className="border-b border-gray-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="h-6 w-6 text-blue-600" />
              Templates
            </CardTitle>
            <Button
              onClick={handleAddClick}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add New
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 w-full">
          {error && templates.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={fetchTemplates} variant="outline">
                Retry
              </Button>
            </div>
          ) : templates.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No templates found.</p>
              <Button onClick={handleAddClick} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="h-4 w-4 mr-2" />
                Add First Template
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto w-full">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 border-b border-gray-200">
                    <TableHead className="font-semibold text-gray-700 p-4">
                      Name
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700 p-4">
                      Template Text
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700 p-4">
                      Modified By
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700 p-4">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id} className="hover:bg-gray-50 border-b border-gray-100">
                      <TableCell className="p-4 font-medium text-gray-900">
                        {template.name}
                      </TableCell>
                      <TableCell className="p-4 text-gray-700" title={template.templateText}>
                        {truncateText(template.templateText)}
                      </TableCell>
                      <TableCell className="p-4 text-gray-700">
                        {getUserName(template.modifiedBy)}
                      </TableCell>
                      <TableCell className="p-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditClick(template)}
                          className="flex items-center gap-1"
                        >
                          <Edit2 className="h-4 w-4" />
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AddEditTemplateModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
        initialData={editingTemplate}
      />
    </div>
  )
}

